import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../../shared/api/client";
import { useUrlFilters } from "../../shared/hooks/urlFilters";
import type {
  EmployeeSummary,
  PageResponse,
  TimesheetHistoryEntry,
  TimesheetStatus,
} from "../../shared/types/api";
import {
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Field,
  Select,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import {
  TIMESHEET_STATUS_LABELS,
  TIMESHEET_STATUS_TONE,
  formatHoursAsTime,
  formatPeriod,
} from "./timesheetVocabulary";

/** Twenty-five rows a page to begin with, server-side: this list grows by a row per person per
 * week for ever, so the reader can ask for more but never gets all of it at once. Both keys are
 * prefixed because this card shares its address with whatever page is hosting it. */
const DEFAULT_PAGE_SIZE = "25";

/* Outside the component so its identity is stable — useUrlFilters memoises on it. */
const HISTORY_FILTERS = {
  person: "", state: "", historyPage: "0", historySize: DEFAULT_PAGE_SIZE,
};

const STATES: TimesheetStatus[] = [
  "DRAFT",
  "SUBMITTED",
  "APPROVED",
  "REJECTED",
  "LOCKED",
];

/**
 * The organisation's timesheet record, for whoever holds TIMESHEET_VIEW_ALL.
 *
 * <p>Separate from the approval queue above it because it answers a different question. The queue
 * is what is outstanding for one approver and empties as they work through it; this is what
 * happened — every period, every state, including the drafts nobody submitted and the weeks that
 * were sent back. A queue that emptied and a person who has logged nothing for a month look
 * identical from the queue alone.
 *
 * <p>The caller decides whether to render this at all, but the permission is enforced on the
 * server: the endpoint refuses anybody without TIMESHEET_VIEW_ALL, so hiding the card is a
 * courtesy rather than the control.
 */
export function TimesheetHistoryCard() {
  const [filters, setFilters] = useUrlFilters(HISTORY_FILTERS);
  const { person, state } = filters;
  const page = Number(filters.historyPage) || 0;
  const pageSize = Number(filters.historySize) || Number(DEFAULT_PAGE_SIZE);

  /* Any filter change returns to the first page: page three of a wider result is empty under a
     narrower one, and an empty table reads as "nothing recorded" rather than "wrong page". */
  const refilter = (patch: Partial<typeof HISTORY_FILTERS>) =>
    setFilters({ ...patch, historyPage: "0" });

  /* Everybody, not just the people with a timesheet on this page — the point of the picker is
     to find somebody who may have recorded nothing at all. */
  const people = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?size=500&sort=fullName,asc",
      ),
  });

  const history = useQuery({
    queryKey: ["timesheets", "history", person, state, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        size: String(pageSize),
      });
      if (person) {
        params.set("employeeId", person);
      }
      if (state) {
        params.set("status", state);
      }
      return api.get<PageResponse<TimesheetHistoryEntry>>(
        `/api/v1/timesheets/history?${params.toString()}`,
      );
    },
  });

  const columns: Column<TimesheetHistoryEntry>[] = [
    {
      key: "employeeName",
      header: "Person",
      render: (row) => <span className="font-medium text-ink-900">{row.employeeName}</span>,
    },
    {
      key: "period",
      header: "Period",
      render: (row) => (
        /* The period is the row's identity, so it is what opens it. */
        <Link
          to={`/timesheets/${row.timesheetId}`}
          className="text-brand-700 hover:underline"
        >
          {formatPeriod(row.periodStart, row.periodEnd)}
        </Link>
      ),
    },
    {
      key: "status",
      header: "State",
      render: (row) => (
        <StatusBadge tone={TIMESHEET_STATUS_TONE[row.status]}>
          {TIMESHEET_STATUS_LABELS[row.status]}
        </StatusBadge>
      ),
    },
    {
      key: "expectedHours",
      header: "Expected",
      className: "text-right tnum",
      render: (row) => formatHoursAsTime(row.expectedHours),
    },
    {
      key: "loggedHours",
      header: "Logged",
      className: "text-right tnum",
      render: (row) => formatHoursAsTime(row.loggedHours),
    },
    {
      key: "decided",
      header: "Decided by",
      render: (row) =>
        row.decidedByName ? (
          <span title={row.decisionNote ?? undefined}>{row.decidedByName}</span>
        ) : (
          <span className="text-ink-400">—</span>
        ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Everybody's history"
        subtitle="Every period on record, whatever state it is in. Only administrators see this."
      />
      <CardBody className="p-0">
        <div className="flex flex-wrap items-end gap-4 border-b border-ink-100 p-4">
          <Field label="Person">
            <Select
              value={person}
              onChange={(event) => refilter({ person: event.target.value })}
            >
              <option value="">Everybody</option>
              {(people.data?.content ?? []).map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.fullName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="State">
            <Select
              value={state}
              onChange={(event) => refilter({ state: event.target.value })}
            >
              <option value="">Every state</option>
              {STATES.map((value) => (
                <option key={value} value={value}>
                  {TIMESHEET_STATUS_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <DataTable
          columns={columns}
          rows={history.data?.content ?? []}
          rowKey={(row) => row.timesheetId}
          loading={history.isPending}
          page={page}
          pageSize={pageSize}
          totalElements={history.data?.totalElements ?? 0}
          onPageChange={(next) => setFilters({ historyPage: String(next) })}
          onPageSizeChange={(next) =>
            setFilters({ historySize: String(next), historyPage: "0" })
          }
          emptyTitle={
            person || state ? "Nothing matches those filters" : "Nothing recorded yet"
          }
          emptyMessage={
            person || state
              ? "No period here belongs to that person, or is in that state."
              : "Once somebody logs time, the period they logged it against appears here."
          }
        />
      </CardBody>
    </Card>
  );
}
